import { Component, OnDestroy, OnInit } from "@angular/core";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { combineLatest, Subject } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { tenureAdder } from "src/app/core/comparable/_data-sources/tenure_extension";
import { currentUser, User } from "src/app/core/mad-auth/mad-auth.store";
import { HotelAssetClassService, HotelComparableAPIService, HotelComparableAssetClass } from "src/app/core/v3/asset-class/hotel";

interface State {
    items: HotelComparableAssetClass[]
}

@Component({
    selector: 'app-hotel-comparable-list',
    templateUrl: './hotel-comparable-list.component.html',
    providers: [ComponentStore]
})
export class HotelComparableListComponent implements OnInit, OnDestroy {
    currentUser: User | null = null
    private _items$ = this._store.select(state => state.items)

    displayedColumns = ['id', 'property_type', 'size', 'consideration', 'source_date', 'actions']
    dataSource$ = combineLatest([
        this._items$,
        this._propertyService.fetchHotelTypes()
    ]).pipe(
        map(([items, types]) => {
            return items.map(item => {
                const type = types.find(t => t.id === item.property_type_id)
                const withDisplayTenures = tenureAdder(item)
                const tenure = withDisplayTenures.display_tenures.length > 0
                    ? withDisplayTenures.display_tenures[0]
                    : null
                return {
                    id: item.id,
                    ref_num: item.ref_num,
                    status: item.status,
                    locationData: {
                        time_offset: null
                    },
                    property_type_name: type ? type.name : 'N/A',
                    size: item.size,
                    display_tenures: withDisplayTenures.display_tenures,
                    used_times: item.used_times,
                    can_be_deleted: item.can_be_deleted,
                    createdBy: item.createdBy,
                    created_at: item.created_at,
                    lastUpdatedBy: item.lastUpdatedBy,
                    updated_at: item.updated_at,
                    tenure
                }
            })
        })
    )

    private _onDestroy$ = new Subject<void>()

    constructor(
        private _store: ComponentStore<State>,
        private _servie: HotelComparableAPIService,
        private _propertyService: HotelAssetClassService,
        private _ngStore: Store
    ) {
        this._store.setState({
            items: []
        })

        this._fetchList()
    }

    ngOnDestroy(): void {
        this._onDestroy$.next()
        this._onDestroy$.complete()
    }

    ngOnInit(): void {
        this._ngStore.select(currentUser).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(user => this.currentUser = user)
    }

    getItemStatusString(status: number): string {
        switch (status) {
            case 0:
                return 'Draft';
            case 2:
                return 'imported';
            default:
                return 'Validated';
        }
    }

    onDelete = this._store.effect<number>(id$ =>
        id$.pipe()
    )

    private _fetchList = this._store.effect<void>(trigger$ =>
        trigger$.pipe(
            switchMap(() =>
                this._servie.fetchList().pipe(
                    tapResponse(
                        (response) => {
                            this._store.patchState({
                                items: response.data
                            })
                        },
                        (err) => {
                            console.error(err)
                        }
                    )
                )
            )
        )
    )
}